import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card } from "@/components/ui/Card";
import { SocialLinksEditor } from "@/components/writer/SocialLinksEditor";
import type { WriterProfile } from "@/lib/api";

interface ProfileEditorProps {
  initial: WriterProfile;
  onSubmit: (data: {
    displayName: string;
    tagline: string;
    bio: string;
    socialLinks: Record<string, string>;
  }) => void;
  loading?: boolean;
}

function socialLinksToArray(
  links: Record<string, string>,
): { platform: string; url: string }[] {
  return Object.entries(links)
    .filter(([, url]) => url)
    .map(([platform, url]) => ({ platform, url }));
}

function socialLinksToMap(
  links: { platform: string; url: string }[],
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const { platform, url } of links) {
    if (url.trim()) result[platform] = url.trim();
  }
  return result;
}

export function ProfileEditor({ initial, onSubmit, loading }: ProfileEditorProps) {
  const [displayName, setDisplayName] = useState(initial.displayName || "");
  const [tagline, setTagline] = useState(initial.tagline || "");
  const [bio, setBio] = useState(initial.bio || "");
  const [socialLinks, setSocialLinks] = useState(() =>
    socialLinksToArray(initial.socialLinks || {}),
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      displayName,
      tagline,
      bio,
      socialLinks: socialLinksToMap(socialLinks),
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card className="p-6 space-y-4">
        <Input
          label="Display Name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Your writer name"
          required
        />
        <Input
          label="Tagline"
          value={tagline}
          onChange={(e) => setTagline(e.target.value)}
          placeholder="Your one-liner (optional)"
        />
        <div className="flex flex-col gap-1">
          <label className="text-sm font-body font-bold text-grey">Bio</label>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Tell readers about yourself... (optional)"
            rows={4}
            className="px-3 py-2 rounded-lg border border-beige/40 bg-white font-body text-sm focus:outline-none focus:ring-2 focus:ring-orange/50 focus:border-orange placeholder:text-beige resize-y"
          />
        </div>
        <SocialLinksEditor links={socialLinks} onChange={setSocialLinks} />
        <Button type="submit" className="w-full" loading={loading}>
          Save Profile
        </Button>
      </Card>
    </form>
  );
}
